import { debounce } from 'lodash';
import { MutableRefObject, useEffect, useRef } from 'react';

const SCROLL_OFFSET = 500;

type UseLazyLoadingOptions = {
  isLoading: boolean;
  loader: (options?: any) => void;
  pageSize: number;
  listLength: number;
  hasNextPage: boolean;
  cleaner: (options?: { avoidEventTypes?: boolean }) => void;
  filterValues: { [key: string]: any };
  scollOffset?: number;
  avoidCleanup?: boolean;
};

const useLazyLoading = ({
  isLoading,
  loader,
  pageSize,
  listLength,
  hasNextPage,
  cleaner = () => {},
  filterValues,
  scollOffset,
  avoidCleanup,
}: UseLazyLoadingOptions): { listRef: MutableRefObject<HTMLDivElement> } => {
  const listRef = useRef<HTMLDivElement>();

  useEffect(() => {
    return avoidCleanup ? () => {} : cleaner;
  }, []);

  useEffect(() => {
    if (!isLoading) {
      if (listLength > 0) {
        cleaner({ avoidEventTypes: true });
      }
      loader({ page: 1, limit: pageSize, ...filterValues });
    }
  }, [filterValues]);

  useEffect(() => {
    const element = listRef.current;
    if (element) {
      element.addEventListener('scroll', handleListScroll);

      return () => {
        element.removeEventListener('scroll', handleListScroll);
        handleListScroll.cancel();
      };
    }
  }, [isLoading, listLength, hasNextPage]);

  const handleListScroll = debounce(() => {
    const element = listRef.current;

    if (!element) {
      return;
    }

    if (
      element.scrollLeft >=
        element.scrollWidth -
          element.clientWidth -
          (scollOffset || SCROLL_OFFSET) &&
      !isLoading &&
      hasNextPage
    ) {
      loader({
        page: Math.ceil(listLength / pageSize) + 1,
        limit: pageSize,
        ...filterValues,
      });
    }
  }, 150);

  return { listRef };
};

export default useLazyLoading;
